import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import styles from '../styles/components/_NewsletterModal.module.scss';

const NewsletterModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    email: '',
    firstname: '',
    name: '',
    phone: ''
  });
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const scrollPositionRef = useRef(0);

  // Gestion du scroll
  useEffect(() => {
    if (isOpen) {
      // Sauvegarder la position actuelle du scroll
      scrollPositionRef.current = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = '100%';
    } else {
      // Restaurer la position du scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollPositionRef.current);
    }

    return () => {
      // Nettoyage au démontage du composant
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollPositionRef.current) {
        window.scrollTo(0, scrollPositionRef.current);
      }
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: null });

    try {
      // Validation de l'email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error('Email invalide');
      }

      console.log('Envoi des données:', formData);
      // Utiliser l'URL directe comme dans NewsletterForm
      const apiUrl = 'https://www.poneyclubtoulouse.fr/scripts/subscribe.php';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email.trim(),
          firstname: formData.firstname.trim(),
          name: formData.name.trim(),
          phone: formData.phone.trim()
        })
      });

      console.log('Statut de la réponse:', response.status);
      console.log('Headers de la réponse:', Object.fromEntries(response.headers.entries()));

      // Lire la réponse comme texte d'abord
      const responseText = await response.text();
      console.log('Réponse brute:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Données parsées:', data);
      } catch (parseError) {
        console.error('Erreur de parsing JSON:', parseError);
        throw new Error('Réponse invalide du serveur');
      }

      if (!response.ok) {
        throw new Error(data.message || 'Une erreur est survenue');
      }

      setStatus({ loading: false, success: true, error: null });
      setFormData({ email: '', firstname: '', name: '', phone: '' });
      
      // Fermer la modale après 2 secondes en cas de succès
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error('Erreur lors de l\'inscription:', error);
      setStatus({
        loading: false,
        success: false,
        error: error.message
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className={styles.modalOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className={styles.modalContent}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <button className={styles.closeButton} onClick={onClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            
            <div className={styles.modalHeader}>
              <h2>Inscrivez-vous à notre newsletter</h2>
              <p>Restez informé des dernières actualités et événements du Poney Club</p>
            </div>

            <form onSubmit={handleSubmit} noValidate className={styles.newsletterForm}>
              {status.success && (
                <div className={styles.successMessage}>
                  <h3>Merci de votre inscription !</h3>
                  <p>Vous recevrez bientôt nos actualités.</p>
                </div>
              )}
              
              {!status.success && (
                <>
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      name="email"
                      placeholder="Votre email *"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={status.loading}
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      name="firstname"
                      placeholder="Votre prénom *"
                      value={formData.firstname}
                      onChange={handleChange}
                      disabled={status.loading}
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      name="name"
                      placeholder="Votre nom *"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={status.loading}
                    />
                  </div>
                  
                  <div className={styles.formGroup}>
                    <input
                      type="text"
                      name="phone"
                      placeholder="Votre téléphone *"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={status.loading}
                    />
                  </div>

                  {status.error && (
                    <div className={styles.errorMessage}>
                      {status.error}
                    </div>
                  )}

                  <button 
                    type="submit" 
                    className="custom-button custom-button--light"
                    disabled={status.loading}
                  >
                    {status.loading ? 'Inscription en cours...' : 'S\'inscrire'}
                  </button>
                </>
              )}
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default NewsletterModal; 